import { cn } from '@/lib/utils';
import { type ComponentPropsWithoutRef, type CSSProperties } from 'react';

// Type-safe style variant types
type HeadlineVariant = 'type-headline-large' | 'type-headline-medium' | 'type-headline-small' | 'type-headline-xsmall';
type HeaderVariant = 'type-header-xlarge' | 'type-header-xlarge-semibold' | 'type-header-large' | 'type-header-medium' | 'type-header-small';
type SubheadingVariant = 'type-subheading-large' | 'type-subheading-medium' | 'type-subheading-small' | 'type-subheading-xsmall' | 'type-subheading-xsmall-semibold';
type BodyVariant = 'type-body-large' | 'type-body-large-bold' | 'type-body-bold' | 'type-card-title' | 'type-card-description' | 'type-section-description' | 'type-body-default' | 'type-caption';
export type TypographyVariant = HeadlineVariant | HeaderVariant | SubheadingVariant | BodyVariant;

// Semantic tag components with variant prop
interface TypographyProps {
    children: React.ReactNode;
    className?: string;
    variant: TypographyVariant;
    style?: CSSProperties;
}

const variantToClassName: Record<TypographyVariant, string> = {
    'type-headline-large': 'scroll-m-20 type-headline-large',
    'type-headline-medium': 'scroll-m-20 type-headline-medium',
    'type-headline-small': 'scroll-m-20 type-headline-small',
    'type-headline-xsmall': 'scroll-m-20 type-headline-xsmall',
    'type-header-xlarge': 'scroll-m-20 type-header-xlarge',
    'type-header-xlarge-semibold': 'scroll-m-20 type-header-xlarge-semibold',
    'type-header-large': 'scroll-m-20 type-header-large',
    'type-header-medium': 'scroll-m-20 type-header-medium',
    'type-header-small': 'scroll-m-20 type-header-small',
    'type-subheading-large': 'type-subheading-large',
    'type-subheading-medium': 'type-subheading-medium',
    'type-subheading-small': 'type-subheading-small',
    'type-subheading-xsmall': 'type-subheading-xsmall',
    'type-subheading-xsmall-semibold': 'type-subheading-xsmall-semibold',
    'type-body-large': 'type-body-large',
    'type-body-large-bold': 'type-body-large-bold',
    'type-body-bold': 'type-body-bold',
    'type-card-title': 'type-card-title',
    'type-card-description': 'type-card-description',
    'type-section-description': 'type-section-description',
    'type-body-default': 'type-body-default',
    'type-caption': 'type-caption',
};

/**
 * H1 component - Always renders semantic h1 tag with any typography variant
 * @example
 * <H1 variant="type-headline-large">Main Title</H1>
 * <H1 variant="type-headline-medium">Alternative Title</H1>
 */
export function H1({
    children,
    className,
    variant,
    style,
    ...props
}: TypographyProps & ComponentPropsWithoutRef<'h1'>) {
    return (
        <h1
            className={cn(variantToClassName[variant], className)}
            style={style}
            {...props}
        >
            {children}
        </h1>
    );
}

/**
 * H2 component - Always renders semantic h2 tag with any typography variant
 * @example
 * <H2 variant="type-headline-large">Section Title</H2>
 * <H2 variant="type-headline-medium">Another Section</H2>
 */
export function H2({
    children,
    className,
    variant,
    style,
    ...props
}: TypographyProps & ComponentPropsWithoutRef<'h2'>) {
    return (
        <h2
            className={cn(variantToClassName[variant], className)}
            style={style}
            {...props}
        >
            {children}
        </h2>
    );
}

/**
 * H3 component - Always renders semantic h3 tag with any typography variant
 * @example
 * <H3 variant="type-headline-small">Subsection Title</H3>
 */
export function H3({
    children,
    className,
    variant,
    style,
    ...props
}: TypographyProps & ComponentPropsWithoutRef<'h3'>) {
    return (
        <h3
            className={cn(variantToClassName[variant], className)}
            style={style}
            {...props}
        >
            {children}
        </h3>
    );
}

/**
 * H4 component - Always renders semantic h4 tag with any typography variant
 * @example
 * <H4 variant="type-headline-xsmall">Small Heading</H4>
 */
export function H4({
    children,
    className,
    variant,
    style,
    ...props
}: TypographyProps & ComponentPropsWithoutRef<'h4'>) {
    return (
        <h4
            className={cn(variantToClassName[variant], className)}
            style={style}
            {...props}
        >
            {children}
        </h4>
    );
}

/**
 * P component - Always renders semantic p tag with any typography variant
 * @example
 * <P variant="type-body-default">Body text</P>
 * <P variant="type-subheading-large">Large subtitle</P>
 */
export function P({
    children,
    className,
    variant,
    style,
    ...props
}: TypographyProps & ComponentPropsWithoutRef<'p'>) {
    return (
        <p
            className={cn(variantToClassName[variant], className)}
            style={style}
            {...props}
        >
            {children}
        </p>
    );
}

// Utility components (don't use variant prop)
interface SimpleTypographyProps {
    children: React.ReactNode;
    className?: string;
    style?: CSSProperties;
}

export function Muted({ children, className, style }: SimpleTypographyProps) {
    return (
        <p
            className={cn(
                'text-muted-foreground text-sm',
                className,
            )}
            style={style}
        >
            {children}
        </p>
    );
}

export function Blockquote({ children, className, style }: SimpleTypographyProps) {
    return (
        <blockquote
            className={cn(
                'mt-6 border-l-2 pl-6 italic',
                className,
            )}
            style={style}
        >
            {children}
        </blockquote>
    );
}

export function InlineCode({ children, className, style }: SimpleTypographyProps) {
    return (
        <code
            className={cn(
                'relative rounded bg-foreground px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold',
                className,
            )}
            style={style}
        >
            {children}
        </code>
    );
}

export function List({ children, className, style }: SimpleTypographyProps) {
    return (
        <ul
            className={cn(
                'my-6 ml-6 list-disc [&>li]:mt-2',
                className,
            )}
            style={style}
        >
            {children}
        </ul>
    );
}

export function Table({ children, className, style }: SimpleTypographyProps) {
    return (
        <div
            className={cn(
                'my-6 w-full overflow-y-auto',
                className,
            )}
            style={style}
        >
            <table className="w-full">
                {children}
            </table>
        </div>
    );
}

// Convenience components for backward compatibility
// These are wrappers around P with predefined variants
export function Subtitle1({ children, className, style, ...props }: SimpleTypographyProps & ComponentPropsWithoutRef<'p'>) {
    return (
        <P variant="type-subheading-large" className={className} style={style} {...props}>
            {children}
        </P>
    );
}

export function Subtitle2({ children, className, style, ...props }: SimpleTypographyProps & ComponentPropsWithoutRef<'p'>) {
    return (
        <P variant="type-subheading-small" className={className} style={style} {...props}>
            {children}
        </P>
    );
}

export function Body1({ children, className, style, ...props }: SimpleTypographyProps & ComponentPropsWithoutRef<'p'>) {
    return (
        <P variant="type-body-default" className={className} style={style} {...props}>
            {children}
        </P>
    );
}

export function Body2({ children, className, style, ...props }: SimpleTypographyProps & ComponentPropsWithoutRef<'p'>) {
    return (
        <P variant="type-caption" className={className} style={style} {...props}>
            {children}
        </P>
    );
}
